import React, { RefObject, useCallback, useState } from 'react';
import FullScreenLoader from '../../../components/loading/FullScreenLoader';
import EsteiraTagsSection from '../components/EsteiraTagsSection';
import FluxoEsteirasSection from '../components/FluxoEsteirasSection';
import ChecklistAdminSection from '../components/ChecklistAdminSection';
import PausasAutomaticasSection from '../components/PausasAutomaticasSection';
import EspecialidadesSection from '../components/EspecialidadesSection';
import SubtarefasSection from '../components/SubtarefasSection';
import MotivosDeParadaSection from '../components/MotivosDeParadaSection';
import AuditoriaComentariosSection from '../components/AuditoriaComentariosSection';
// Esqueleto de página compartilhado (`recepcao-conte-do`, `recepcao-header-row`,
// `recepcao-title`) — é o que faz título, margens e responsividade baterem com as demais
// telas. O import é explícito de propósito: depender de CSS carregado por outra tela é
// acoplamento invisível, lição do BUG-V74 (at-67).
import '../../recepcao/pages/Recepcao.css';
import './css/adminSistema.css';

/**
 * Configuração do módulo OFICINA (rota `/admin/sistema/oficina`).
 *
 * Antes da at-73 este arquivo tinha 1001 linhas: era a Administração do Sistema
 * inteira, com as abas de Tags de Esteira e Fluxos de Esteiras escritas inline
 * (state, handlers e JSX) enquanto Checklist, Pausas Automáticas e Especialidades já
 * eram components. As duas foram extraídas e a página ficou sendo só o que ela
 * deveria ser: o seletor de abas do módulo.
 *
 * A escolha do módulo acontece um nível acima, em `AdminSistemaHub` — cada módulo tem
 * a sua rota e o seu RoleGuard.
 *
 * ⚠️ Cada aba é responsável pela própria ajuda ("Como Funciona") e pelos próprios
 * dados; esta página não conhece nem uma nem outra. O único acoplamento que resta é o
 * loader de tela cheia, que as abas de Tags e Fluxos informam por `onLoadingChange`
 * (as outras três já cuidavam do próprio estado de carga antes da at-73).
 */

interface AdministracaoSistemaProps {
  sidebarRef?: RefObject<HTMLDivElement>;
}

type Aba =
  | 'esteira-tags'
  | 'fluxo-esteiras'
  | 'checklist'
  | 'pausas-automaticas'
  | 'especialidades'
  | 'subtarefas'
  | 'motivos-de-parada'
  | 'auditoria-comentarios';

const AdministracaoSistemaPage: React.FC<AdministracaoSistemaProps> = () => {
  const [tabActive, setTabActive] = useState<Aba>('esteira-tags');
  const [loading, setLoading] = useState(true);

  // A aba ativa informa a própria carga; trocar de aba zera o estado para não herdar
  // o loading da anterior (as abas que não informam nada nunca ficam presas no loader).
  const handleLoadingChange = useCallback((valor: boolean) => setLoading(valor), []);

  const trocarAba = (aba: Aba) => {
    setTabActive(aba);
    setLoading(false);
  };

  return (
    <div
      className="Frame1000004012"
      style={{
        height: loading ? '100vh' : 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: loading ? 'center' : 'space-between',
        alignItems: loading ? 'center' : 'flex-start',
        backgroundColor: loading ? '#808080' : 'transparent',
      }}
    >
      {loading && <FullScreenLoader />}

      {/*
        ⚠️ O conteúdo é SEMPRE montado e apenas escondido durante a carga. Antes da
        at-73 a página calculava o loading a partir do estado interno das duas abas
        inline, então podia trocar `{!loading && ...}` sem problema. Com as abas em
        components, quem reporta a carga é a própria aba (`onLoadingChange`) — e
        desmontar o conteúdo enquanto `loading` é `true` impediria a aba de montar,
        de disparar o efeito e de reportar `false`: a tela ficaria no loader para
        sempre. Esconder em vez de desmontar preserva o visual e sai do impasse.
      */}
      <div
        className="ConteDo recepcao-conte-do"
        style={{ display: loading ? 'none' : 'flex' }}
      >
          {/* Header */}
          <div className="recepcao-header-row">
            <div className="recepcao-title">Administração do Sistema - Oficina</div>
          </div>

          {/* Abas Horizontais */}
          <div className="admin-tabs-container">
            <button
              className={`admin-tab-btn ${tabActive === 'esteira-tags' ? 'active' : ''}`}
              onClick={() => trocarAba('esteira-tags')}
            >
              Tags de Esteira
            </button>
            <button
              className={`admin-tab-btn ${tabActive === 'fluxo-esteiras' ? 'active' : ''}`}
              onClick={() => trocarAba('fluxo-esteiras')}
            >
              Fluxos de Esteiras
            </button>
            <button
              className={`admin-tab-btn ${tabActive === 'checklist' ? 'active' : ''}`}
              onClick={() => trocarAba('checklist')}
            >
              Checklist
            </button>
            <button
              className={`admin-tab-btn ${tabActive === 'pausas-automaticas' ? 'active' : ''}`}
              onClick={() => trocarAba('pausas-automaticas')}
            >
              Pausas Automáticas
            </button>
            <button
              className={`admin-tab-btn ${tabActive === 'especialidades' ? 'active' : ''}`}
              onClick={() => trocarAba('especialidades')}
            >
              Especialidades
            </button>
            <button
              className={`admin-tab-btn ${tabActive === 'subtarefas' ? 'active' : ''}`}
              onClick={() => trocarAba('subtarefas')}
            >
              Sub-tarefas
            </button>
            <button
              className={`admin-tab-btn ${tabActive === 'motivos-de-parada' ? 'active' : ''}`}
              onClick={() => trocarAba('motivos-de-parada')}
            >
              Motivos de parada
            </button>
            <button
              className={`admin-tab-btn ${tabActive === 'auditoria-comentarios' ? 'active' : ''}`}
              onClick={() => trocarAba('auditoria-comentarios')}
            >
              Auditoria e Comentários
            </button>
          </div>

          {tabActive === 'esteira-tags' && <EsteiraTagsSection onLoadingChange={handleLoadingChange} />}

          {tabActive === 'fluxo-esteiras' && <FluxoEsteirasSection onLoadingChange={handleLoadingChange} />}

          {/* ─── ABA: CHECKLIST (estruturas dinâmicas + permissões) ──────────── */}
          {tabActive === 'checklist' && <ChecklistAdminSection />}

          {/* ─── ABA: PAUSAS AUTOMÁTICAS (at-48) ─────────────────────────────── */}
          {tabActive === 'pausas-automaticas' && <PausasAutomaticasSection />}

          {/* at-62: especialidades de colaborador (ADR 0029). */}
          {tabActive === 'especialidades' && <EspecialidadesSection />}

          {/* at-89: catálogo de sub-tarefas por esteira (ADR 0041). */}
          {tabActive === 'subtarefas' && <SubtarefasSection />}

          {/* at-134: o catálogo dos motivos da parada do serviço (ADR 0065). */}
          {tabActive === 'motivos-de-parada' && <MotivosDeParadaSection />}

          {/* at-90: quem vê qual aba da Auditoria + quem escreve comentários (ADR 0042). */}
          {tabActive === 'auditoria-comentarios' && <AuditoriaComentariosSection />}
      </div>
    </div>
  );
};

export default AdministracaoSistemaPage;
